import { useState, useMemo, useEffect, useCallback } from 'react';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import { saUsers, type SaUser } from '@/mocks/saUsers';
import UserStatsBar from './components/UserStatsBar';
import UserFilters from './components/UserFilters';
import UserTable from './components/UserTable';
import UserDetailModal from './components/UserDetailModal';

type LoadingState = 'loading' | 'ready' | 'error';

function simulateLoad(): Promise<SaUser[]> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (Math.random() > 0.96) {
        reject(new Error('Error al conectar con el Identity Engine. Verificá la conexión.'));
      } else {
        resolve(saUsers);
      }
    }, 500 + Math.random() * 300);
  });
}

export default function SAUsersManagement() {
  const [users, setUsers] = useState<SaUser[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<SaUser | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [activeUserType, setActiveUserType] = useState('all');
  const [activeStatus, setActiveStatus] = useState('all');
  const [activeCountry, setActiveCountry] = useState('all');
  const [activeRadio, setActiveRadio] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadData = useCallback(() => {
    setLoadingState('loading');
    setErrorMsg(null);
    simulateLoad()
      .then((data) => {
        setUsers(data);
        setLoadingState('ready');
      })
      .catch((err) => {
        setErrorMsg(err.message || 'Error desconocido');
        setLoadingState('error');
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Derived lists for filters
  const countries = useMemo(() => [...new Set(users.map((u) => u.country))].sort(), [users]);
  const radios = useMemo(() => [...new Set(users.map((u) => u.associatedRadio).filter((r) => r !== '—'))].sort(), [users]);

  // Counts
  const userTypeCounts = useMemo(() => {
    const c: Record<string, number> = { all: users.length };
    users.forEach((u) => {
      c[u.userType] = (c[u.userType] || 0) + 1;
    });
    return c;
  }, [users]);

  const statusCounts = useMemo(() => {
    const c: Record<string, number> = { all: users.length };
    users.forEach((u) => {
      c[u.accountStatus] = (c[u.accountStatus] || 0) + 1;
    });
    return c;
  }, [users]);

  // Filtered users
  const filteredUsers = useMemo(() => {
    let result = users;
    if (activeUserType !== 'all') result = result.filter((u) => u.userType === activeUserType);
    if (activeStatus !== 'all') result = result.filter((u) => u.accountStatus === activeStatus);
    if (activeCountry !== 'all') result = result.filter((u) => u.country === activeCountry);
    if (activeRadio !== 'all') result = result.filter((u) => u.associatedRadio === activeRadio);
    if (dateFrom) result = result.filter((u) => u.registrationDate >= dateFrom);
    if (dateTo) result = result.filter((u) => u.registrationDate <= dateTo);
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      result = result.filter(
        (u) =>
          u.name.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q) ||
          u.phone.toLowerCase().includes(q) ||
          u.associatedRadio.toLowerCase().includes(q) ||
          u.country.toLowerCase().includes(q) ||
          u.city.toLowerCase().includes(q) ||
          u.mainRole.toLowerCase().includes(q),
      );
    }
    return result;
  }, [users, activeUserType, activeStatus, activeCountry, activeRadio, dateFrom, dateTo, searchTerm]);

  const activeCount = users.filter((u) => u.accountStatus === 'activo').length;
  const suspendedCount = users.filter((u) => u.accountStatus === 'suspendido').length;
  const blockedCount = users.filter((u) => u.accountStatus === 'bloqueado').length;
  const pendingCount = users.filter((u) => u.accountStatus === 'pendiente').length;
  const verifiedCount = users.filter((u) => u.verificationStatus === 'verificado').length;
  const unverifiedCount = users.filter((u) => u.verificationStatus === 'no_verificado' || u.verificationStatus === 'pendiente').length;

  const handleAction = (action: string, user: SaUser) => {
    switch (action) {
      case 'view':
        setSelectedUser(user);
        break;
      case 'view_activity':
      case 'view_reports':
      case 'view_moderation':
      case 'view_payments':
        setSelectedUser(user);
        break;
      default:
        // For suspend/reactivate/block/verify/reset/close_sessions — show modal
        setSelectedUser(user);
    }
  };

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1800px] mx-auto">
            {/* Page header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Global Users Management</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  {loadingState === 'ready'
                    ? `${filteredUsers.length} de ${users.length} usuarios · Todos los roles y tipos`
                    : 'Cargando datos...'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={loadData}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-50 border border-background-200 text-foreground-500 text-xs font-medium cursor-pointer hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-refresh-line text-sm"></i>
                  Refrescar
                </button>
              </div>
            </div>

            {/* Loading state */}
            {loadingState === 'loading' && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: 7 }).map((_, i) => (
                    <div key={i} className="bg-background-50 border border-background-200 rounded-xl p-3 min-w-[100px]">
                      <div className="h-3 bg-background-200 rounded w-12 mb-2 animate-pulse"></div>
                      <div className="h-5 bg-background-200 rounded w-8 animate-pulse"></div>
                    </div>
                  ))}
                </div>
                <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1600px]">
                      <thead>
                        <tr className="border-b border-background-200 bg-background-100/50">
                          {['Usuario', 'Email', 'Teléfono', 'Tipo', 'Rol', 'Radio', 'País', 'Estado', 'Verificación', 'Registro', 'Último Acceso', ''].map((h) => (
                            <th key={h} className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: 10 }).map((_, i) => (
                          <tr key={i} className="border-b border-background-200/60">
                            {Array.from({ length: 12 }).map((_, j) => (
                              <td key={j} className="px-3 py-3">
                                <div className="h-3 bg-background-200 rounded animate-pulse" style={{ width: `${40 + Math.random() * 80}px` }}></div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Error state */}
            {loadingState === 'error' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
                  <i className="ri-error-warning-line text-red-400 text-2xl"></i>
                </div>
                <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar usuarios</p>
                <p className="text-foreground-400 text-xs mb-4">{errorMsg}</p>
                <button
                  onClick={loadData}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line text-sm"></i>
                  Reintentar
                </button>
              </div>
            )}

            {/* Stats bar */}
            {loadingState === 'ready' && (
              <UserStatsBar
                total={users.length}
                activeCount={activeCount}
                suspendedCount={suspendedCount}
                blockedCount={blockedCount}
                pendingCount={pendingCount}
                verifiedCount={verifiedCount}
                unverifiedCount={unverifiedCount}
              />
            )}

            {/* Info banner */}
            {loadingState === 'ready' && (
              <div className="bg-accent-50 border border-accent-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0">
                    <i className="ri-group-line text-accent-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-accent-800 text-xs font-semibold">Global Users Management</p>
                    <p className="text-accent-600/80 text-[10px]">Gestión centralizada de todos los usuarios de RadioFrecuenciaWeb. Preparado para conexión con Identity Engine y Supabase.</p>
                  </div>
                </div>
                <span className="px-2.5 py-1 rounded-full bg-accent-200 text-accent-700 text-[10px] font-medium whitespace-nowrap flex items-center gap-1 self-start sm:self-auto">
                  <i className="ri-database-2-line text-[9px]"></i>
                  Mock Data — Listo para conectar
                </span>
              </div>
            )}

            {/* Filters */}
            {loadingState === 'ready' && (
              <UserFilters
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                activeUserType={activeUserType}
                onUserTypeChange={setActiveUserType}
                activeStatus={activeStatus}
                onStatusChange={setActiveStatus}
                activeCountry={activeCountry}
                onCountryChange={setActiveCountry}
                activeRadio={activeRadio}
                onRadioChange={setActiveRadio}
                dateFrom={dateFrom}
                onDateFromChange={setDateFrom}
                dateTo={dateTo}
                onDateToChange={setDateTo}
                userTypeCounts={userTypeCounts}
                statusCounts={statusCounts}
                countries={countries}
                radios={radios}
              />
            )}

            {/* Table */}
            {loadingState === 'ready' && (
              <UserTable
                users={filteredUsers}
                onSelect={setSelectedUser}
                onAction={handleAction}
              />
            )}
          </div>
        </main>
      </div>

      {/* Detail Modal */}
      <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} />

      {/* Mobile bottom hint */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">
          Super Admin · Global Users Management
        </p>
      </div>
    </div>
  );
}